/**
 * Modal displaying patient phone numbers with Dialpad call integration.
 * @agentic connections
 */

import classes from "./PatientProfileCard.module.css";

// Components
import { Modal } from "@benchmark/ui/components/atomic";
import { Stack, Text, Group, ScrollArea } from "@mantine/core";
import { PhoneNumberDisplay } from "@/components/shared/detail-views/PhoneNumberDisplay";

// Hooks
import { useCallback } from "react";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";

// Utils
import { createPhoneCallLink } from "@/utils/ui/dialpad-utils";
import { showErrorNotification } from "@benchmark/ui/utils";

// Types
import type { PhoneNumberInfo } from "@benchmark/core/resolvers";

/**
 * Props for the phone numbers modal.
 * @agentic connections
 */
export type PhoneNumbersModalProps = {
  opened: boolean;
  onClose: () => void;
  phoneNumbers: PhoneNumberInfo[];
  patientId: string;
};

/**
 * Modal for viewing and calling patient phone numbers.
 * @agentic connections
 */
export const PhoneNumbersModal = ({
  opened,
  onClose,
  phoneNumbers,
  patientId,
}: PhoneNumbersModalProps) => {
  const { practitionerRole } = useCurrentPractitionerRole();

  const handleCall = useCallback(
    (phoneNumber: string) => {
      if (!practitionerRole?.id) {
        showErrorNotification(
          "Practitioner role not found. Please contact support if this issue persists.",
          { title: "Unable to Place Call" },
        );
        return;
      }

      // Ask user to confirm they want to place the call
      const confirmed = window.confirm(
        `Are you sure you want to place a call to ${phoneNumber}?`,
      );

      if (!confirmed) {
        return;
      }

      try {
        const callUrl = createPhoneCallLink({
          phoneNumber,
          patientId,
          practitionerRole, // Auto-extracts ID and from number
        });
        window.open(callUrl, "_blank");
      } catch (error) {
        showErrorNotification(
          error instanceof Error
            ? error.message
            : "Unable to initiate call. Please try again.",
          { title: "Call Failed" },
        );
      }
    },
    [practitionerRole, patientId],
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Text className={classes.name}>Make a Call</Text>
          <Text className={classes.profileText}>
            Select a phone number to call for this patient.
          </Text>
        </Group>
      }
      size="md"
      centered
    >
      {phoneNumbers.length === 0 ? (
        <Text c="dimmed" size="sm">
          No phone numbers available for this patient.
        </Text>
      ) : (
        <ScrollArea style={{ maxHeight: "400px" }}>
          <Stack gap="md">
            {phoneNumbers.map((phoneInfo, index) => (
              <PhoneNumberDisplay
                key={`${phoneInfo.value}-${index}`}
                phoneNumber={phoneInfo.value}
                phoneType={phoneInfo.phoneType}
                roleLabel={phoneInfo.roleLabel}
                contactName={phoneInfo.contactName}
                isPrimary={phoneInfo.isPrimary}
                onCallClick={() => handleCall(phoneInfo.value)}
                ariaLabel={`${phoneInfo.contactName || "Patient"} phone number`}
              />
            ))}
          </Stack>
        </ScrollArea>
      )}
    </Modal>
  );
};
